import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { FaGoogle } from 'react-icons/fa';
import { useAuth } from '../contexts/AuthContext';
import { useLocation, useNavigate } from 'react-router-dom';
import { createCheckoutSession, SUBSCRIPTION_PLANS } from '../services/stripe';
import { useLanguage } from '../i18n/LanguageContext';
import { trackLandingEvent } from '../services/analytics';

const Container = styled.div`
  max-width: 600px;
  margin: 120px auto 40px;
  padding: 40px;
  background: white;
  border-radius: 12px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  text-align: center;
`;

const Title = styled.h1`
  font-size: 2rem;
  color: #2D3748;
  margin-bottom: 1.5rem;
`;

const Description = styled.p`
  color: #4A5568;
  margin-bottom: 2.5rem;
  font-size: 1.1rem;
  line-height: 1.6;
`;

const GoogleButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  padding: 0.75rem 1.5rem;
  background: white;
  border: 1px solid #dadce0;
  border-radius: 4px;
  color: #3c4043;
  font-size: 1rem;
  font-weight: 500;
  cursor: pointer;
  transition: background-color 0.2s;
  margin: 0 auto;
  
  &:hover {
    background-color: #f8f9fa;
  }
  
  svg {
    font-size: 18px;
    color: #4285f4;
  }
`;

const Login: React.FC = () => {
  const { user, signInWithGoogle } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useLanguage();
  const [intendedSubscription, setIntendedSubscription] = useState<string | null>(null);
  const query = new URLSearchParams(location.search);
  const loginSource = query.get('from') || 'direct';
  const loginIntent = query.get('intent') || undefined;

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }
    setIntendedSubscription(sessionStorage.getItem('intendedSubscription'));
  }, []);
  
  useEffect(() => {
    // 如果用户已登录并且存在意图订阅
    if (user && intendedSubscription) {
      const handleSubscription = async () => {
        try {
          // 清除会话存储
          if (typeof window !== 'undefined') {
            sessionStorage.removeItem('intendedSubscription');
          }
          
          // 获取计划信息
          const plan = SUBSCRIPTION_PLANS.find(p => p.id === intendedSubscription);
          
          if (!plan) {
            navigate('/pricing');
            return;
          }
          
          // 免费计划直接跳转到订阅页面
          if (plan.id === 'free') {
            navigate('/subscription');
            return;
          }
          
          // 付费计划创建结账会话
          await createCheckoutSession(
            user.uid, 
            plan.stripePriceId,
            `${window.location.origin}/subscription?success=true`,
            `${window.location.origin}/pricing`
          );
        } catch (error) {
          console.error('Error handling subscription after login:', error);
          navigate('/pricing');
        }
      };
      
      handleSubscription();
    } else if (user) {
      if (loginIntent === 'trial') {
        navigate('/pricing');
        return;
      }
      // 如果用户已登录但没有订阅意图，直接跳转到首页
      navigate('/');
    }
  }, [user, intendedSubscription, navigate, loginIntent]);
  
  const handleLogin = async () => {
    trackLandingEvent('login_start', { placement: 'login_page', source: loginSource, intent: loginIntent });
    try {
      await signInWithGoogle();
      trackLandingEvent('login_success', { placement: 'login_page', source: loginSource, intent: loginIntent });
      // 登录成功后，上面的useEffect会处理重定向
    } catch (error) {
      console.error('Login failed:', error);
      trackLandingEvent('login_failed', { placement: 'login_page', source: loginSource, intent: loginIntent });
    }
  };
  
  return (
    <Container>
      <Title>{t('login.title')}</Title>
      <Description>
        {t('login.description')}
      </Description>
      <GoogleButton onClick={handleLogin}>
        <FaGoogle />
        {t('login.button')}
      </GoogleButton>
    </Container>
  );
};

export default Login; 
